import mfaInfoLogo from "@assets/images/mfa-info.svg";
import mfaLockedOutLogo from "@assets/images/mfa-locked-out.svg";
import {
  Box,
  Card,
  CircularProgress,
  Divider,
  Paper,
  Typography
} from "@material-ui/core";
import useNotifier from "@saleor/hooks/useNotifier";
import {
  useMfaPhoneSendCode,
  useMfaSendEmailCode
} from "@saleor/mfa/mutations";
import Cookies from "js-cookie";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import useStyles from "./styles";

interface MfaLockedOutPageProps {
  userEmail: string;
  intervalMs?: number;
  lockoutDuration?: number;
  isEmailLockedOut?: boolean;
}

const MfaLockedOutPage: React.FC<MfaLockedOutPageProps> = ({
  userEmail,
  isEmailLockedOut = true,
  intervalMs = 1000,
  lockoutDuration = 600
}) => {
  const classes = useStyles({});
  const navigate = useNavigate();

  const getSavedTargetTime = () => {
    const savedTime = Cookies.get("mfaTargetTime");
    return savedTime ? new Date(savedTime) : null;
  };

  const initializeTargetTime = () => {
    const savedTargetTime = getSavedTargetTime();
    if (savedTargetTime && savedTargetTime > new Date()) {
      return savedTargetTime;
    }
    const newTargetTime = new Date(
      new Date().getTime() + lockoutDuration * 1000
    );
    Cookies.set("mfaTargetTime", newTargetTime.toISOString(), {
      expires: lockoutDuration / 86400
    });
    return newTargetTime;
  };

  const [targetTime] = useState(initializeTargetTime);
  const [timeLeft, setTimeLeft] = useState(() => calculateTimeLeft(targetTime));
  const [isClickable, setIsClickable] = useState(
    !!Cookies.get("mfaTargetTime")
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      const newTimeLeft = calculateTimeLeft(targetTime);
      setTimeLeft(newTimeLeft);

      if (newTimeLeft.minutes === 0 && newTimeLeft.seconds === 0) {
        setIsClickable(false);
      }
    }, intervalMs);

    return () => clearInterval(interval);
  }, [intervalMs, targetTime]);

  function calculateTimeLeft(target: Date) {
    const now = new Date().getTime();
    const difference = target.getTime() - now;

    if (difference <= 0) {
      Cookies.remove("mfaTargetTime");

      return { minutes: 0, seconds: 0 };
    }

    const minutes = Math.floor(difference / 60000);
    const seconds = Math.floor((difference % 60000) / 1000);

    return { minutes, seconds };
  }

  const formatTime = (num: number) => num.toString().padStart(2, "0");
  const notify = useNotifier();
  const [sendPhoneCode] = useMfaPhoneSendCode({});
  const [sendEmailCode] = useMfaSendEmailCode({});

  const handleTrySmsAuth = async () => {
    setIsLoading(true);
    if (!isClickable) {
      try {
        const response = await sendPhoneCode({
          variables: { email: userEmail }
        });

        const mfaPhoneSendCode = response.data?.mfaPhoneSendCode;
        let mfaMessage = mfaPhoneSendCode?.message;

        const PhoneResendCooldown = ResendCooldown => {
          const now = Math.floor(Date.now() / 1000);
          const lockoutUntil = now + ResendCooldown;
          Cookies.set("phoneResendCooldown", lockoutUntil.toString());
        };

        PhoneResendCooldown(mfaPhoneSendCode?.data.lockoutPeriod);

        Cookies.set("isPhoneResendCooldown", "true");

        if (typeof mfaMessage === "string" && mfaMessage.startsWith("['")) {
          try {
            mfaMessage = JSON.parse(mfaMessage.replace(/'/g, '"'));
          } catch (error) {
            console.error("Error parsing MFA message:", error);
          }
        }

        if (
          !mfaPhoneSendCode.success &&
          mfaPhoneSendCode.message === "MFA704"
        ) {
          notify({
            status: "error",
            text: "Too many failed attempts."
          });

          return;
        }

        if (mfaPhoneSendCode?.success) {
          setIsLoading(false);

          const cooldownSeconds = mfaPhoneSendCode.data.resendCooldown || 300;
          Cookies.set("mfaCodeSent", "true", {
            expires: cooldownSeconds / 86400
          });
          Cookies.set("mfaCreatedAt", mfaPhoneSendCode.data.createdAt!, {
            expires: cooldownSeconds / 86400
          });
          Cookies.set("mfaUserEmail", userEmail!, {
            expires: cooldownSeconds / 86400
          });
          Cookies.set("mfaResendCooldown", cooldownSeconds.toString(), {
            expires: cooldownSeconds / 86400
          });

          navigate("/mfa-phone-input-page/", {
            state: {
              createdAt: mfaPhoneSendCode.data.createdAt!,
              email: userEmail!,
              isEmailSendCode: false,
              isPhoneSendCode: true
            }
          });
        } else {
          console.error("MFA Email Code Sending Failed:", mfaMessage);

          if (mfaMessage === "MFA704" && Cookies.get("mfaCodeSent")) {
            notify({
              status: "warning",
              text:
                "Check your email for the code. Redirecting to the Authentication Code page in 3 seconds..."
            });

            setTimeout(() => {
              navigate("/mfa-phone-input-page/", {
                state: {
                  createdAt: Cookies.get("mfaCreatedAt"),
                  email: Cookies.get("mfaUserEmail"),
                  isEmailSendCode: false,
                  isPhoneSendCode: true
                }
              });
            }, 3000);
          } else {
            notify({
              status: "error",
              text: "MFA Email Code Sending Failed"
            });
          }
        }
      } catch (err) {
        console.error("Error sending email code:", err);
      }
    }
  };

  const handleTryEmailAuth = async () => {
    setIsLoading(true);
    try {
      const response = await sendEmailCode({
        variables: { email: userEmail }
      });

      const mfaResponse = response.data?.mfaSendCode;
      let mfaMessage = mfaResponse?.message;

      const EmailResendCooldown = ResendCooldown => {
        const now = Math.floor(Date.now() / 1000);
        const lockoutUntil = now + ResendCooldown;
        Cookies.set("emailResendCooldown", lockoutUntil.toString());
      };

      EmailResendCooldown(mfaResponse?.data.lockoutPeriod);

      if (typeof mfaMessage === "string" && mfaMessage.startsWith("['")) {
        try {
          mfaMessage = JSON.parse(mfaMessage.replace(/'/g, '"'));
        } catch (error) {
          console.error("Error parsing MFA message:", error);
        }
      }

      if (!mfaResponse.success && mfaResponse.message === "MFA704") {
        notify({
          status: "error",
          text: `Too many failed attempts`
        });

        return;
      }

      if (mfaResponse?.success) {
        setIsLoading(false);

        const cooldownSeconds = mfaResponse.data.resendCooldown || 300;
        Cookies.set("mfaCodeSent", "true", {
          expires: cooldownSeconds / 86400
        });
        Cookies.set("mfaCreatedAt", mfaResponse.data.createdAt!, {
          expires: cooldownSeconds / 86400
        });
        Cookies.set("mfaUserEmail", userEmail!, {
          expires: cooldownSeconds / 86400
        });
        Cookies.set("mfaResendCooldown", cooldownSeconds.toString(), {
          expires: cooldownSeconds / 86400
        });

        navigate("/mfa-email-input-page/", {
          state: {
            createdAt: mfaResponse.data.createdAt!,
            email: userEmail!,
            isEmailSendCode: true,
            isPhoneSendCode: false
          }
        });
      } else {
        console.error("MFA Email Code Sending Failed:", mfaMessage);

        if (mfaMessage === "MFA704" && Cookies.get("mfaCodeSent")) {
          notify({
            status: "warning",
            text:
              "Check your email for the code. Redirecting to the Authentication Code page in 3 seconds..."
          });

          setTimeout(() => {
            navigate("/mfa-email-input-page/", {
              state: {
                createdAt: Cookies.get("mfaCreatedAt"),
                email: Cookies.get("mfaUserEmail"),
                isEmailSendCode: true,
                isPhoneSendCode: false
              }
            });
          }, 3000);
        } else {
          notify({
            status: "error",
            text: "MFA Email Code Sending Failed"
          });
        }
      }
    } catch (err) {
      console.error("Error sending email code:", err);
    }
  };

  return (
    <Card className={classes.cardLockedOut}>
      <img
        className={classes.infoLogoLockedOut}
        src={mfaLockedOutLogo}
        alt="Locked Out"
      />
      <Typography
        variant="h5"
        component="h5"
        className={classes.titleLockedOut}
      >
        You were locked out
      </Typography>
      <Typography className={classes.subtitleLockedOut}>
        Your account has been temporarily locked due to
        <br /> multiple failed authentication attempts.
        {timeLeft.minutes !== 0 || timeLeft.seconds !== 0 ? (
          <>
            <br /> Please wait{" "}
            <b>
              {formatTime(timeLeft.minutes)}:{formatTime(timeLeft.seconds)}s
            </b>{" "}
            before trying again.
          </>
        ) : (
          <>
            <br /> Please try another authentication method.
          </>
        )}
      </Typography>
      <Paper className={classes.infoCardNoBorderColor} elevation={0}>
        <img className={classes.infoLogo} src={mfaInfoLogo} alt="MFA info" />
        <Typography style={{ fontSize: 12 }} align="left">
          <b>Need help?</b> Reach out to{" "}
          <b>smmallsonline.acquisition@smsupermalls.com</b>
        </Typography>
      </Paper>
      <Box display="flex" flexDirection="column" alignItems="center">
        <Divider style={{ width: "100%" }} />
        <Typography
          className={classes.textResendCode}
          style={{
            color: isClickable || isLoading ? "#A0A0A0" : "#247CFF",
            cursor: isClickable || isLoading ? "not-allowed" : "pointer",
            fontWeight: 500
          }}
          onClick={
            !isClickable && !isLoading
              ? isEmailLockedOut
                ? handleTrySmsAuth
                : handleTryEmailAuth
              : undefined
          }
        >
          {isEmailLockedOut
            ? "Try SMS Authentication"
            : "Try Email Authentication"}
          {isLoading && (
            <CircularProgress
              size={18}
              className={classes.circularProgress}
              style={{
                color: isClickable || isLoading ? "#A0A0A0" : "#247CFF"
              }}
            />
          )}
        </Typography>
      </Box>
    </Card>
  );
};

export default MfaLockedOutPage;
